import {
  Body,
  Button,
  Heading,
  Icon,
  IconButton,
  IconType,
} from '@assembly-js/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaWhatsapp } from 'react-icons/fa';

import {
  REFERRAL_PAID_CREDITS,
  REFERRAL_SIGNUP_CREDITS,
} from 'src/constants/referralConsts';
import { useAppSelector } from 'src/hooks/useStore';
// The app's shared modal, as the other product modals use.
// eslint-disable-next-line no-restricted-imports
import { ModalWrapper } from 'src/legacy/components/Modals';
import { useGetReferralsQuery } from 'src/services/api/referralApi';

import { ReferralsHero } from '../ReferralsHero';
import { getReferralSummary } from '../referralSummary';
import { useCopyReferralLink } from '../useCopyReferralLink';

// Figma: 448px popup (305px on mobile) with an 8px radius and no border;
// the shared modal's paper is 4px and full-width on phones.
const POPUP_WIDTH_PX = 448;
const MOBILE_POPUP_WIDTH_PX = 305;
const POPUP_PAPER_SX = {
  '& .MuiDialog-paper': {
    borderRadius: '8px',
    m: 2,
    maxWidth: 'calc(100% - 32px)',
    '@media (max-width: 639px)': { width: MOBILE_POPUP_WIDTH_PX },
  },
};
// Preflight is off, so fixed-size boxes here opt into border-box to keep
// Figma's outer dimensions.
const SHARE_BUTTON_CLASS =
  'box-border flex h-11 flex-1 items-center justify-center rounded border border-solid border-gray-150 bg-gray-150 text-light-gray max-sm:h-[28.45px] max-sm:rounded-[2.6px]';

type ShareTarget = {
  id: string;
  label: string;
  href: string;
  icon: IconType | 'whatsapp';
  // Figma sizes the glyphs individually on desktop; mobile uses 13px for all.
  iconClassName: string;
};

const ShareIcon = ({ target }: { target: ShareTarget }) =>
  target.icon === 'whatsapp' ? (
    // The design system has no WhatsApp glyph; this is the brand mark.
    <FaWhatsapp className={target.iconClassName} />
  ) : (
    <Icon className={target.iconClassName} icon={target.icon} />
  );

type ReferralPromoModalProps = {
  open: boolean;
  onClose: () => void;
};

export function ReferralPromoModal({ open, onClose }: ReferralPromoModalProps) {
  const { t } = useTranslation();
  const { referralLink, copyReferralLink } = useCopyReferralLink();
  const userRef = useAppSelector((state) => state.user.ref);
  const { data: referrals = [] } = useGetReferralsQuery(userRef, {
    skip: !open,
  });
  const { creditsEarned } = getReferralSummary(referrals);

  const shareTargets = useMemo<ShareTarget[]>(() => {
    const shareText = t(($) => $.page_settings_referrals.popup.share_text, {
      signupCredits: REFERRAL_SIGNUP_CREDITS,
    });
    const message = encodeURIComponent(`${shareText} ${referralLink}`);
    return [
      {
        // LinkedIn's offsite share can't prefill text, so the feed composer
        // carries the link inside the message instead.
        id: 'linkedin',
        label: t(($) => $.page_settings_referrals.popup.share_on_linkedin),
        href: `https://www.linkedin.com/feed/?shareActive=true&text=${message}`,
        icon: 'LinkedIn',
        iconClassName: 'size-[17px] max-sm:size-[13px]',
      },
      {
        // Figma puts Instagram here, but it has no web share link, so this
        // slot keeps WhatsApp.
        id: 'whatsapp',
        label: t(($) => $.page_settings_referrals.popup.share_on_whatsapp),
        href: `https://wa.me/?text=${message}`,
        icon: 'whatsapp',
        iconClassName: 'size-4 max-sm:size-[13px]',
      },
      {
        id: 'x',
        label: t(($) => $.page_settings_referrals.popup.share_on_x),
        href: `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(referralLink)}`,
        icon: 'X',
        iconClassName: 'size-4 max-sm:size-[13px]',
      },
      {
        id: 'email',
        label: t(($) => $.page_settings_referrals.popup.share_via_email),
        href: `mailto:?subject=${encodeURIComponent(
          t(($) => $.page_settings_referrals.popup.email_subject),
        )}&body=${message}`,
        icon: 'Email',
        iconClassName: 'size-[17px] max-sm:size-[13px]',
      },
    ];
  }, [t, referralLink]);

  const steps = [
    t(($) => $.page_settings_referrals.popup.step_share),
    t(($) => $.page_settings_referrals.popup.step_earn, {
      signupCredits: REFERRAL_SIGNUP_CREDITS,
      paidCredits: REFERRAL_PAID_CREDITS,
    }),
    t(($) => $.page_settings_referrals.popup.step_they_get, {
      signupCredits: REFERRAL_SIGNUP_CREDITS,
    }),
  ];

  return (
    <ModalWrapper
      disableGutters
      hideActions
      hideDividers
      noBorder
      onClose={onClose}
      open={open}
      sx={POPUP_PAPER_SX}
      width={POPUP_WIDTH_PX}
    >
      <ReferralsHero variant="popup" />
      {/* Mobile spacing follows where Figma draws the credits card, which
          overflows its 49px layout slot by 3px on each side. */}
      <div className="flex flex-col gap-6 px-4 pb-8 pt-4 max-sm:gap-[17px] max-sm:px-3.5 max-sm:pb-[14.5px] max-sm:pt-2.5">
        <div className="box-border flex h-[57px] flex-col rounded border border-solid border-non-hover-border px-3 py-2 max-sm:h-[55px] max-sm:px-2 max-sm:py-1.5">
          <Body
            className="leading-[16px] text-[#6a7282] max-sm:!text-[11px] max-sm:leading-[15px]"
            size="xs"
            tag="span"
          >
            {t(($) => $.page_settings_referrals.popup.credits_earned)}
          </Body>
          <Heading
            className="!mb-0 !text-[15px] leading-[24px] text-[#101828] max-sm:!text-[14px] max-sm:pt-1 max-sm:leading-[26px] max-sm:tracking-[-0.415px]"
            size="base"
            tag="span"
          >
            {creditsEarned}
          </Heading>
        </div>

        <div className="flex flex-col gap-5 max-sm:gap-3">
          <div className="flex flex-col gap-1.5">
            <Body
              className="leading-[20px] tracking-[-0.15px] text-[#4a5565]"
              size="sm"
              tag="span"
            >
              {t(($) => $.page_settings_referrals.popup.share_label)}
            </Body>
            <div className="flex items-center gap-2">
              <div className="box-border flex h-8 min-w-0 flex-1 items-center rounded border border-solid border-non-hover-border px-2 max-sm:h-[34px]">
                <Body
                  className="truncate leading-[20px] text-black-heading"
                  size="sm"
                  tag="div"
                >
                  {referralLink}
                </Body>
              </div>
              {/* The design-system buttons set their own display, padding and
                  border, so the overrides on those need `!`. */}
              <Button
                className="box-border w-[90px] shrink-0 !border-black/[0.12] max-sm:!hidden"
                label={t(($) => $.page_settings_referrals.popup.copy)}
                onClick={() => {
                  void copyReferralLink();
                }}
                variant="primary"
              />
              <IconButton
                aria-label={t(($) => $.page_settings_referrals.popup.copy)}
                className="box-border h-[34px] w-[38px] shrink-0 !border-black/[0.12] !p-0 sm:!hidden [&_svg]:!size-3"
                icon="Copy"
                onClick={() => {
                  void copyReferralLink();
                }}
                variant="primary"
              />
            </div>
          </div>

          <div className="flex gap-2">
            {shareTargets.map((target) => (
              <a
                aria-label={target.label}
                className={SHARE_BUTTON_CLASS}
                href={target.href}
                key={target.id}
                rel="noopener noreferrer"
                target={target.id === 'email' ? undefined : '_blank'}
              >
                <ShareIcon target={target} />
              </a>
            ))}
          </div>

          {/* The mobile frame drops the explainer to keep the popup short. */}
          <div className="flex flex-col gap-5 max-sm:hidden">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-[#e5e7eb]" />
              <Body
                className="leading-[16px] text-[#99a1af]"
                size="sm"
                tag="span"
              >
                {t(($) => $.page_settings_referrals.popup.how_it_works)}
              </Body>
              <div className="h-px flex-1 bg-[#e5e7eb]" />
            </div>
            <ul className="m-0 flex list-disc flex-col gap-2 pl-[21px] pt-3">
              {steps.map((step) => (
                <li className="text-[#364153]" key={step}>
                  <Body className="leading-[20px]" size="base" tag="span">
                    {step}
                  </Body>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
}
